import { useState } from 'react';
import { Sheet } from '../shared/Sheet';
import { Icon } from '../chrome/Icon';
import { useT } from '../../i18n';
import { useCreateFeedback } from '../../data/queries';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function FeedbackSheet({ open, onClose }: Props) {
  const t = useT();
  const createFeedback = useCreateFeedback();

  const [type, setType] = useState<'bug' | 'feature'>('bug');
  const [area, setArea] = useState('Milestones');
  const [message, setMessage] = useState('');
  const [canContact, setCanContact] = useState(false);
  const [touched, setTouched] = useState(false);
  const [success, setSuccess] = useState(false);

  const messageValid = message.trim().length > 0;
  const formValid = messageValid;

  const handleSubmit = () => {
    setTouched(true);
    if (!formValid || createFeedback.isPending) return;

    createFeedback.mutate(
      {
        feedback_type: type,
        area,
        message: message.trim(),
        can_contact: canContact,
      },
      {
        onSuccess: () => {
          setSuccess(true);
          setTimeout(() => {
            // Reset state and close sheet
            setType('bug');
            setArea('Milestones');
            setMessage('');
            setCanContact(false);
            setTouched(false);
            setSuccess(false);
            onClose();
          }, 2000);
        },
        onError: (err) => {
          console.error('Failed to submit feedback:', err);
        },
      }
    );
  };

  const areas = [
    { value: 'Milestones', label: t('milestones.title', 'Milestones') },
    { value: 'Leaps', label: t('leaps.title', 'Leaps') },
    { value: 'Media', label: t('profile.mediaGallery', 'Media') },
    { value: 'Dashboard', label: t('nav.dashboard', 'Dashboard') },
    { value: 'Today', label: t('today.title', 'Today') },
    { value: 'Activities', label: t('activities.title', 'Activities') },
    { value: 'Other', label: t('common.other', 'Other') },
  ];

  return (
    <Sheet open={open} onClose={onClose} size="md" maxHeight="92dvh" label={t('profile.feedback', 'Report bug or suggest feature')}>
      <div className="flex flex-col gap-7 px-6 pb-8 pt-0 overflow-y-auto">
        {/* Close-row */}
        <div className="flex items-center justify-between">
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
            {t('profile.feedback', 'Feedback')}
          </span>
          <button
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
            className="-mr-2.5 flex min-h-11 min-w-11 items-center justify-center"
          >
            <Icon name="close" size={22} color="var(--color-ink)" />
          </button>
        </div>

        {success ? (
          <div className="flex flex-col items-center justify-center py-12 text-center animate-fade-in">
            <div className="flex size-16 items-center justify-center rounded-full bg-[#dff7e6] text-[#1a7f37] mb-4">
              <Icon name="check" size={32} color="currentColor" />
            </div>
            <h2 className="font-serif text-[24px] leading-tight text-ink mb-2">
              {t('feedback.successTitle', 'Thank you!')}
            </h2>
            <p className="text-[14.5px] text-ink-2 max-w-[280px]">
              {t('feedback.successDesc', 'Your feedback has been successfully submitted. We appreciate your help in improving BabyNook!')}
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-serif text-[28px] leading-tight tracking-tight text-ink">
              {t('feedback.title', 'Help us improve.')}
            </h1>
            <p className="text-[14.5px] leading-relaxed text-ink-2 text-pretty">
              {t('feedback.description', 'Found a bug? Or have an idea for a new feature? Let us know below.')}
            </p>

            {/* Type selection: Bug vs Feature */}
            <div>
              <span className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3 mb-2.5">
                {t('feedback.typeLabel', 'Feedback Type')}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setType('bug')}
                  className={[
                    'flex flex-1 min-h-12 items-center justify-center gap-2 rounded-2xl text-[14.5px] transition-all border',
                    type === 'bug'
                      ? 'border-[#B5523B] bg-[#ffebe9]/40 font-semibold text-[#7E3320]'
                      : 'border-line bg-surface font-medium text-ink-2 hover:bg-bg/50',
                  ].join(' ')}
                >
                  <span className={['size-2.5 rounded-full', type === 'bug' ? 'bg-[#B5523B]' : 'bg-ink-3'].join(' ')} />
                  {t('feedback.typeBug', 'Bug Report')}
                </button>
                <button
                  type="button"
                  onClick={() => setType('feature')}
                  className={[
                    'flex flex-1 min-h-12 items-center justify-center gap-2 rounded-2xl text-[14.5px] transition-all border',
                    type === 'feature'
                      ? 'border-accent bg-accent-soft font-semibold text-accent-ink'
                      : 'border-line bg-surface font-medium text-ink-2 hover:bg-bg/50',
                  ].join(' ')}
                >
                  <span className={['size-2.5 rounded-full', type === 'feature' ? 'bg-accent' : 'bg-ink-3'].join(' ')} />
                  {t('feedback.typeFeature', 'Feature Suggestion')}
                </button>
              </div>
            </div>

            {/* App Area Dropdown */}
            <div>
              <label htmlFor="fb-area" className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
                {t('feedback.areaLabel', 'Area of the App')}
              </label>
              <div className="mt-2.5 relative flex min-h-12 items-center rounded-xl border border-line bg-surface px-4 focus-within:border-ink">
                <select
                  id="fb-area"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  className="w-full border-0 bg-transparent py-3 text-[15px] text-ink outline-none appearance-none pr-8 cursor-pointer"
                >
                  {areas.map((opt) => (
                    <option key={opt.value} value={opt.value} className="bg-surface text-ink">
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div className="absolute right-4 pointer-events-none flex items-center">
                  <Icon name="chevron" size={14} color="var(--color-ink-2)" />
                </div>
              </div>
            </div>

            {/* Message/Description */}
            <div>
              <label htmlFor="fb-message" className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
                {t('feedback.messageLabel', 'Description')}
              </label>
              <textarea
                id="fb-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  type === 'bug'
                    ? t('feedback.messagePlaceholderBug', 'What happened? What did you expect to happen?')
                    : t('feedback.messagePlaceholderFeature', 'Describe the feature you would like to see and why.')
                }
                rows={5}
                className={[
                  'mt-2.5 w-full rounded-xl border bg-surface px-4 py-3 text-[15px] text-ink outline-none transition-colors resize-none min-h-[120px]',
                  touched && !messageValid ? 'border-[#B5523B]' : 'border-line focus:border-ink',
                ].join(' ')}
              />
              {touched && !messageValid && (
                <p className="mt-1.5 text-[12px] text-[#7E3320]">{t('feedback.messageError', 'Please enter a details description.')}</p>
              )}
            </div>

            {/* Contact permission checkbox */}
            <div className="flex items-start gap-3 mt-1">
              <label className="relative flex cursor-pointer items-center rounded-full p-0.5" htmlFor="fb-contact">
                <input
                  type="checkbox"
                  className="before:content-[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded border border-line transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-accent checked:bg-accent hover:before:opacity-10"
                  id="fb-contact"
                  checked={canContact}
                  onChange={(e) => setCanContact(e.target.checked)}
                />
                <span className="absolute text-bg opacity-0 transition-opacity peer-checked:opacity-100 top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" stroke="currentColor" strokeWidth="1">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                </span>
              </label>
              <label htmlFor="fb-contact" className="text-[13.5px] leading-snug text-ink-2 select-none cursor-pointer mt-0.5">
                {t('feedback.contactCheckbox', 'Allow BabyNook team to contact you via email for more details.')}
              </label>
            </div>

            {createFeedback.isError && (
              <p className="text-[12px] text-[#7E3320] mt-1">
                {t('feedback.submitError', 'Failed to submit. Please try again.')}
              </p>
            )}

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={createFeedback.isPending}
              className="
                mt-2 inline-flex w-full min-h-14 items-center justify-center gap-2.5
                rounded-3xl bg-ink px-6 text-[15.5px] font-semibold text-bg
                disabled:opacity-50 hover:bg-ink/90 active:scale-[0.98] transition-all
              "
            >
              {createFeedback.isPending ? t('common.submitting', 'Submitting…') : t('common.submit', 'Submit')}
              {!createFeedback.isPending && <Icon name="arrow" size={18} color="currentColor" />}
            </button>
          </>
        )}
      </div>
    </Sheet>
  );
}
